import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FiX, FiFileText, FiCpu, FiDownload, FiCopy, FiCheck, FiAlertCircle, FiBook, FiSave, FiRefreshCw, FiClock, FiPrinter, FiRotateCcw } from 'react-icons/fi';
import {
  generateDocumentation,
  getSavedDocumentation,
  saveDocumentation,
  getDocumentationHistory,
  type DocumentationResult,
  type DocumentationHistoryEntry,
} from '../../api/ai';

// ── Mermaid generator from React Flow nodes/edges ────────────────────────────
function toMermaidId(id: string): string {
  return 'n_' + id.replace(/[^a-zA-Z0-9]/g, '_');
}

function buildMermaidFromDiagram(nodes: any[], edges: any[]): string {
  const resources = nodes.filter(n => n.data?.type);
  const getLabel = (n: any) => (n.data?.label || n.data?.type || n.id).replace(/"/g, "'");

  // Group children by parentNode
  const childrenOf: Record<string, any[]> = {};
  const standalone: any[] = [];
  resources.forEach(n => {
    if (n.parentNode) {
      if (!childrenOf[n.parentNode]) childrenOf[n.parentNode] = [];
      childrenOf[n.parentNode].push(n);
    } else {
      standalone.push(n);
    }
  });

  const lines: string[] = ['graph TD'];

  // Subgraphs for group containers
  Object.entries(childrenOf).forEach(([groupId, children]) => {
    const groupNode = nodes.find(n => n.id === groupId);
    const groupLabel = (groupNode?.data?.label || 'Groupe').replace(/"/g, "'");
    lines.push(`  subgraph ${toMermaidId(groupId)}["${groupLabel}"]`);
    children.forEach(n => {
      lines.push(`    ${toMermaidId(n.id)}["${getLabel(n)}"]`);
    });
    lines.push('  end');
  });

  // Standalone resources (not in any group)
  standalone.forEach(n => {
    lines.push(`  ${toMermaidId(n.id)}["${getLabel(n)}"]`);
  });

  // Edges
  edges.forEach(e => {
    const src = toMermaidId(e.source);
    const tgt = toMermaidId(e.target);
    lines.push(`  ${src} --> ${tgt}`);
  });

  return lines.join('\n');
}

function injectMermaidInDoc(techDoc: string, mermaid: string): string {
  const schemaSection = `\n## Schéma de l'Architecture\n\n\`\`\`mermaid\n${mermaid}\n\`\`\`\n`;
  // Insert after the first H1 heading line
  const firstH1 = techDoc.match(/^# .+$/m);
  if (firstH1 && firstH1.index !== undefined) {
    const insertAt = firstH1.index + firstH1[0].length;
    return techDoc.slice(0, insertAt) + schemaSection + techDoc.slice(insertAt);
  }
  return schemaSection + techDoc;
}

// ── Simple Markdown → HTML converter ─────────────────────────────────────────
function markdownToHtml(md: string): string {
  // Step 1: protect fenced code blocks FIRST (before any inline processing)
  const codeBlocks: string[] = [];
  let html = md.replace(/```(mermaid)?\n([\s\S]*?)```/g, (_match, lang, content) => {
    const idx = codeBlocks.length;
    if (lang === 'mermaid') {
      codeBlocks.push(`<div class="mermaid-block"><pre class="mermaid">${content}</pre></div>`);
    } else {
      codeBlocks.push(`<pre class="code-block"><code>${content}</code></pre>`);
    }
    return `\x00CODE${idx}\x00`;
  });

  // Step 2: inline code (safe now — no fenced blocks remain)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Step 3: headings, hr, bold/italic
  html = html
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^---$/gm, '<hr/>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Step 4: tables
  html = html.replace(/((?:^\|.+\|\s*$\n?)+)/gm, (block) => {
    const rows = block.trim().split('\n').filter(r => r.trim());
    const isHeaderSep = (r: string) => /^\|[-| :]+\|$/.test(r.trim());
    let result = '<table><thead>';
    let inBody = false;
    rows.forEach((row) => {
      if (isHeaderSep(row)) { result += '</thead><tbody>'; inBody = true; return; }
      const cells = row.split('|').filter((_, ci, arr) => ci > 0 && ci < arr.length - 1);
      const tag = !inBody ? 'th' : 'td';
      result += '<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
    });
    result += inBody ? '</tbody></table>' : '</table>';
    return result;
  });

  // Step 5: blockquotes, lists, paragraphs
  html = html
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/((?:^[-*] .+\n?)+)/gm, (block) => {
      const items = block.trim().split('\n').map(l => `<li>${l.replace(/^[-*] /, '')}</li>`).join('');
      return `<ul>${items}</ul>`;
    })
    .replace(/((?:^\d+\. .+\n?)+)/gm, (block) => {
      const items = block.trim().split('\n').map(l => `<li>${l.replace(/^\d+\. /, '')}</li>`).join('');
      return `<ol>${items}</ol>`;
    })
    .replace(/^(?!<[a-z\x00]).+$/gm, (line) => line.trim() ? `<p>${line}</p>` : '')
    .replace(/\n{3,}/g, '\n\n');

  // Step 6: restore code blocks
  html = html.replace(/\x00CODE(\d+)\x00/g, (_, idx) => codeBlocks[+idx]);

  return html;
}

function buildIframeDoc(title: string, markdown: string): string {
  const body = markdownToHtml(markdown);
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${title}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: #1a1a2e; max-width: 900px; margin: 0 auto; padding: 24px; background: #fff; }
  h1 { font-size: 2rem; color: #1e3a5f; border-bottom: 3px solid #3b82f6; padding-bottom: 12px; margin-top: 32px; }
  h2 { font-size: 1.5rem; color: #1e40af; border-left: 4px solid #3b82f6; padding-left: 12px; margin-top: 28px; }
  h3 { font-size: 1.2rem; color: #1d4ed8; margin-top: 24px; }
  h4 { font-size: 1rem; color: #374151; margin-top: 20px; }
  h5, h6 { font-size: 0.9rem; color: #6b7280; }
  table { border-collapse: collapse; width: 100%; margin: 16px 0; font-size: 0.9rem; }
  th { background: #1e40af; color: #fff; padding: 10px 14px; text-align: left; }
  td { padding: 8px 14px; border-bottom: 1px solid #e5e7eb; }
  tr:nth-child(even) td { background: #f8fafc; }
  code { background: #f1f5f9; color: #e11d48; padding: 2px 6px; border-radius: 4px; font-size: 0.88em; font-family: 'Fira Code', monospace; }
  pre.code-block { background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px; overflow-x: auto; margin: 16px 0; font-size: 0.85rem; }
  pre.code-block code { background: none; color: inherit; padding: 0; }
  div.mermaid-block { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin: 16px 0; }
  .mermaid svg { max-width: 100%; height: auto; }
  pre.mermaid { background: none; color: #374151; font-size: 0.85rem; }
  blockquote { border-left: 4px solid #3b82f6; margin: 16px 0; padding: 8px 16px; background: #eff6ff; color: #1e40af; border-radius: 0 8px 8px 0; }
  ul, ol { padding-left: 24px; margin: 8px 0; }
  li { margin: 4px 0; }
  hr { border: none; border-top: 2px solid #e5e7eb; margin: 32px 0; }
  p { margin: 8px 0; }
  strong { color: #111827; }
</style></head><body>${body}
<script src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js" onload="
  mermaid.initialize({ startOnLoad: false, theme: 'default', securityLevel: 'loose' });
  document.querySelectorAll('pre.mermaid').forEach(function(el) {
    var div = document.createElement('div');
    div.className = 'mermaid';
    div.textContent = el.textContent;
    el.parentNode.replaceChild(div, el);
  });
  mermaid.run();
"></script>
</body></html>`;
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface DocumentationModalProps {
  isOpen: boolean;
  onClose: () => void;
  architectureId?: string;
  nodes: any[];
  edges: any[];
  provider: string;
  environment?: string;
  architectureName?: string;
  projectName?: string;
  costs?: any;
}

type Tab = 'tech' | 'adr';

export const DocumentationModal: React.FC<DocumentationModalProps> = ({
  isOpen, onClose, architectureId,
  nodes, edges, provider, environment, architectureName, projectName, costs
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DocumentationResult | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('tech');
  const [viewMode, setViewMode] = useState<'preview' | 'markdown'>('preview');
  const [copied, setCopied] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<DocumentationHistoryEntry[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const initializedRef = useRef(false);

  const generate = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setIsDirty(false);
    try {
      const res = await generateDocumentation({ nodes, edges, provider, environment, architectureName, projectName, costs });
      const mermaid = buildMermaidFromDiagram(nodes, edges);
      res.techDoc = injectMermaidInDoc(res.techDoc, mermaid);
      setResult(res);
      setIsDirty(true); // generated but not yet saved
      setSavedAt(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  }, [nodes, edges, provider, environment, architectureName, projectName, costs, loading]);

  // On open: try to load saved doc first, else auto-generate
  useEffect(() => {
    if (!isOpen || initializedRef.current) return;
    initializedRef.current = true;

    if (architectureId) {
      setLoading(true);
      getSavedDocumentation(architectureId)
        .then(saved => {
          setResult({
            techDoc: saved.tech_doc,
            adrDoc: saved.adr_doc,
            metadata: saved.metadata,
          });
          setSavedAt(saved.updated_at);
          setIsDirty(false);
          setLoading(false);
        })
        .catch(() => {
          // No saved doc — auto-generate
          setLoading(false);
          generate();
        });
    } else {
      generate();
    }
  }, [isOpen]);

  // Reset on close so next open re-initializes
  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen]);

  // Update iframe when tab/result/viewMode changes
  useEffect(() => {
    if (!iframeRef.current || !result || viewMode !== 'preview') return;
    const title = activeTab === 'tech'
      ? `Documentation — ${architectureName || 'Architecture'}`
      : `ADR — ${architectureName || 'Architecture'}`;
    const md = activeTab === 'tech' ? result.techDoc : result.adrDoc;
    iframeRef.current.srcdoc = buildIframeDoc(title, md);
  }, [result, activeTab, viewMode, architectureName]);

  const handleSave = async () => {
    if (!architectureId || !result || saving) return;
    setSaving(true);
    try {
      await saveDocumentation(architectureId, {
        techDoc: result.techDoc,
        adrDoc: result.adrDoc,
        metadata: result.metadata,
      });
      setSavedAt(new Date().toISOString());
      setIsDirty(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = () => {
    setResult(null);
    setSavedAt(null);
    generate();
  };

  const currentMarkdown = result ? (activeTab === 'tech' ? result.techDoc : result.adrDoc) : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(currentMarkdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadMd = () => {
    const blob = new Blob([currentMarkdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab === 'tech' ? 'doc_technique' : 'adr'}_${(architectureName || 'architecture').replace(/\s+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    const title = activeTab === 'tech' ? `Documentation — ${architectureName}` : `ADR — ${architectureName}`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(buildIframeDoc(title, currentMarkdown));
    w.document.close();
    // Wait for mermaid to render before printing
    setTimeout(() => w.print(), 1200);
  };

  const handleShowHistory = async () => {
    if (showHistory) { setShowHistory(false); return; }
    if (!architectureId) return;
    setHistLoading(true);
    try {
      const res = await getDocumentationHistory(architectureId);
      setHistory(res.history);
    } catch {
      setHistory([]);
    } finally {
      setHistLoading(false);
      setShowHistory(true);
    }
  };

  const handleRestoreHistory = (entry: DocumentationHistoryEntry) => {
    setResult({ techDoc: entry.techDoc, adrDoc: entry.adrDoc, metadata: entry.metadata });
    setSavedAt(entry.savedAt);
    setIsDirty(true);
    setShowHistory(false);
  };

  const handleDownloadHtml = () => {
    const title = activeTab === 'tech' ? `Documentation — ${architectureName}` : `ADR — ${architectureName}`;
    const blob = new Blob([buildIframeDoc(title, currentMarkdown)], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab === 'tech' ? 'doc_technique' : 'adr'}_${(architectureName || 'architecture').replace(/\s+/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (!isOpen) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} />

      <div
        style={{
          position: 'relative', zIndex: 1,
          width: '92vw', maxWidth: 1100, height: '90vh',
          background: '#fff', borderRadius: 16, display: 'flex', flexDirection: 'column',
          overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.35)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)', color: '#fff', flexShrink: 0 }}>
          <FiBook size={22} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Documentation IA</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{projectName} {architectureName ? `› ${architectureName}` : ''} · {provider?.toUpperCase()}</div>
          </div>
          {/* Saved / dirty indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            {savedAt && !isDirty && (
              <span style={{ opacity: 0.75, display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiClock size={11} /> Sauvegardé le {formatDate(savedAt)}
              </span>
            )}
            {isDirty && (
              <span style={{ background: 'rgba(251,191,36,0.25)', color: '#fbbf24', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                Non sauvegardé
              </span>
            )}
            {result && (
              <span style={{ opacity: 0.6, marginLeft: 8 }}>
                {result.metadata.tokensUsed.toLocaleString()} tokens · {(result.metadata.durationMs / 1000).toFixed(1)}s
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Tabs + Actions bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Doc tabs */}
          <div style={{ display: 'flex', gap: 4, flex: 1 }}>
            {([['tech', FiFileText, 'Doc Technique'], ['adr', FiBook, 'ADR']] as const).map(([id, Icon, label]) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as Tab)}
                disabled={!result}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                  borderRadius: 8, border: 'none', cursor: result ? 'pointer' : 'default',
                  background: activeTab === id ? '#1e40af' : 'transparent',
                  color: activeTab === id ? '#fff' : '#6b7280',
                  fontWeight: activeTab === id ? 600 : 400, fontSize: 13,
                }}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          {/* View toggle */}
          {result && (
            <div style={{ display: 'flex', background: '#e5e7eb', borderRadius: 8, padding: 2, gap: 2 }}>
              {(['preview', 'markdown'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  style={{
                    padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    background: viewMode === mode ? '#fff' : 'transparent',
                    color: viewMode === mode ? '#1e40af' : '#6b7280',
                    fontSize: 12, fontWeight: viewMode === mode ? 600 : 400,
                    boxShadow: viewMode === mode ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {mode === 'preview' ? 'Aperçu' : 'Markdown'}
                </button>
              ))}
            </div>
          )}

          {/* Actions */}
          {result && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={handleCopy}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151' }}
              >
                {copied ? <FiCheck size={13} color="#16a34a" /> : <FiCopy size={13} />}
                {copied ? 'Copié !' : 'Copier'}
              </button>
              <button
                onClick={handleDownloadMd}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151' }}
              >
                <FiDownload size={13} /> .md
              </button>
              <button
                onClick={handleDownloadHtml}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151' }}
              >
                <FiDownload size={13} /> .html
              </button>

              {/* Export PDF */}
              <button
                onClick={handleExportPdf}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151' }}
              >
                <FiPrinter size={13} /> PDF
              </button>

              {/* Historique */}
              {architectureId && (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={handleShowHistory}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: `1px solid ${showHistory ? '#6366f1' : '#d1d5db'}`, background: showHistory ? '#eef2ff' : '#fff', cursor: 'pointer', fontSize: 12, color: showHistory ? '#4f46e5' : '#374151' }}
                  >
                    <FiClock size={13} /> Historique
                  </button>
                  {showHistory && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, width: 300, background: '#fff', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', zIndex: 100, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Versions précédentes</span>
                        <button onClick={() => setShowHistory(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}>×</button>
                      </div>
                      {histLoading ? (
                        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>Chargement…</div>
                      ) : history.length === 0 ? (
                        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>Aucune version précédente</div>
                      ) : (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 220, overflowY: 'auto' }}>
                          {history.map((entry, i) => (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f9fafb' }}>
                              <div>
                                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#374151' }}>Version {history.length - i}</p>
                                <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>{formatDate(entry.savedAt)}</p>
                                {entry.metadata?.tokensUsed && <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>{entry.metadata.tokensUsed.toLocaleString()} tokens</p>}
                              </div>
                              <button
                                onClick={() => handleRestoreHistory(entry)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#4f46e5', background: '#eef2ff', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                              >
                                <FiRotateCcw size={10} /> Restaurer
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Regénérer */}
              <button
                onClick={handleRegenerate}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: loading ? 'default' : 'pointer', fontSize: 12, color: '#374151', opacity: loading ? 0.5 : 1 }}
              >
                <FiRefreshCw size={13} /> Regénérer
              </button>

              {/* Sauvegarder */}
              {architectureId && (
                <button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px',
                    borderRadius: 8, border: 'none',
                    background: savedOk ? '#16a34a' : isDirty ? '#1e40af' : '#9ca3af',
                    color: '#fff', cursor: saving || !isDirty ? 'default' : 'pointer',
                    fontSize: 12, fontWeight: 600,
                    transition: 'background 0.2s',
                  }}
                >
                  {savedOk ? <FiCheck size={13} /> : saving ? <FiCpu size={13} /> : <FiSave size={13} />}
                  {savedOk ? 'Sauvegardé !' : saving ? 'Sauvegarde…' : 'Sauvegarder'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {/* Loading */}
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, background: '#fff' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', border: '4px solid #e5e7eb', borderTopColor: '#1e40af', animation: 'spin 0.8s linear infinite' }} />
              <div style={{ color: '#1e40af', fontWeight: 600, fontSize: 16 }}>Génération en cours…</div>
              <div style={{ color: '#6b7280', fontSize: 13 }}>Claude analyse l'architecture et génère la documentation</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <FiAlertCircle size={40} color="#ef4444" />
              <div style={{ color: '#ef4444', fontWeight: 600, textAlign: 'center', maxWidth: 500 }}>{error}</div>
              <button
                onClick={() => { setError(null); generate(); }}
                style={{ padding: '8px 20px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
              >
                Réessayer
              </button>
            </div>
          )}

          {/* Preview iframe */}
          {result && !loading && viewMode === 'preview' && (
            <iframe
              ref={iframeRef}
              title="documentation-preview"
              style={{ width: '100%', height: '100%', border: 'none' }}
              sandbox="allow-same-origin allow-scripts"
            />
          )}

          {/* Markdown raw */}
          {result && !loading && viewMode === 'markdown' && (
            <pre style={{
              margin: 0, padding: 24, height: '100%', overflow: 'auto', boxSizing: 'border-box',
              background: '#0f172a', color: '#e2e8f0', fontFamily: "'Fira Code', 'Cascadia Code', monospace",
              fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word'
            }}>
              {currentMarkdown}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentationModal;
